/**
 * BenefitsSection — Homepage benefits of applying through Legacy Triangle
 * Design: Green accent, 4 benefit cards with icons, professional layout
 */

import React from "react";
import { CheckCircle2, Clock, Shield, Zap } from "lucide-react";
import { Link } from "react-router-dom";

const benefits = [
  {
    icon: Zap,
    title: "Fast Approval",
    description: "Get approved in as little as 24-48 hours with our streamlined application process and expert review team.",
  },
  {
    icon: Shield,
    title: "Transparent Terms",
    description: "No hidden fees, no surprises. We believe in complete transparency from application to funding.",
  },
  {
    icon: CheckCircle2,
    title: "Expert Guidance",
    description: "Our funding specialists guide you through every step, ensuring you get the right solution for your business.",
  },
  {
    icon: Clock,
    title: "Dedicated Support",
    description: "Access to our team throughout the entire process. We're here to answer questions and support your success.",
  },
];

export default function BenefitsSection() {
  return (
    <section data-testid="benefits-section" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div
            style={{ fontFamily: "'Montserrat', sans-serif" }}
            className="text-[#48d480] text-sm font-bold tracking-widest uppercase mb-3"
          >
            Why Choose Us
          </div>
          <h2
            style={{ fontFamily: "'Montserrat', sans-serif" }}
            className="text-4xl md:text-5xl font-black text-gray-900 mb-4 leading-tight"
          >
            Benefits of Applying Through <span className="text-[#48d480]">Legacy Triangle</span>
          </h2>
          <p
            style={{ fontFamily: "'Inter', sans-serif" }}
            className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed"
          >
            We've streamlined the funding process to make it faster, easier, and more transparent than ever before.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8 hover:shadow-lg transition-shadow duration-300 border border-gray-200"
              >
                {/* Icon */}
                <div className="mb-4">
                  <div className="w-14 h-14 bg-[#48d480] rounded-lg flex items-center justify-center">
                    <Icon size={28} className="text-white" />
                  </div>
                </div>

                {/* Title */}
                <h3
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                  className="text-xl font-bold text-gray-900 mb-3"
                >
                  {benefit.title}
                </h3>

                {/* Description */}
                <p
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  className="text-gray-600 leading-relaxed text-sm"
                >
                  {benefit.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p
            style={{ fontFamily: "'Inter', sans-serif" }}
            className="text-gray-600 mb-6 text-lg"
          >
            Ready to unlock your business potential?
          </p>
          <Link
            to="/apply"
            data-testid="benefits-apply-btn"
            className="inline-block bg-[#48d480] hover:bg-[#3ab370] text-white font-bold px-8 py-4 rounded-lg transition-colors shadow-lg shadow-green-900/20"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Start Your Application Today
          </Link>
        </div>
      </div>
    </section>
  );
}
